import type { EntryMeta } from '@app-types/sys'
import { Box } from '@components/box'
import { Details } from '@components/details'
import { Arrow } from '@components/icons/arrow'
import { JsonLd } from '@components/json-ld'
import { Container } from '@components/layout/grid'
import { List } from '@components/list'
import { Markdown } from '@components/markdown'
import { Paragraph } from '@components/paragraph'
import { Text } from '@components/text'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import { RichText } from '@contentful-components/rich-text'
import type { CustomRenderers } from '@contentful-components/rich-text/render'
import { useSectionOfFAQs } from '@graphql/hooks/section-of-faqs'
import { EMPTY_COLLECTION, EMPTY_SECTION } from '@graphql/placeholders'
import type { FaqSectionFragment } from '@graphql/queries/GetSectionOfFAQs.graphql'
import { titleToAnchor } from '@lib/utils/faq'
import classNames from 'classnames'
import Link from 'next/link'
import { createContext, type FunctionComponent, useContext } from 'react'
import type { FAQPage } from 'schema-dts'

import styles from './section-of-faqs.module.scss'

/**
 * When false, individual SectionOfFAQs instances skip rendering their own
 * FAQPage JSON-LD. Use this on pages that aggregate all FAQ items into a
 * single FAQPage schema (e.g. /faq) to avoid the "Duplicate field FAQPage"
 * structured-data error reported by Google Search Console.
 */
export const FaqSchemaContext = createContext(true)

export const SectionOfFAQs: FunctionComponent<FaqSectionFragment> = ({
    sys,
    type,
    heading,
    moreLink,
    ...restOfProps
}) => {
    if (restOfProps?.faqs) {
        return (
            <SectionOfFAQResolved
                sys={sys}
                type={type}
                heading={heading}
                moreLink={moreLink}
                {...restOfProps}
            />
        )
    }
    return <SelfFetchingSectionOfFAQs sys={sys} type={type} />
}

const SelfFetchingSectionOfFAQs: FunctionComponent<EntryMeta> = ({ sys }) => {
    const { section } = useSectionOfFAQs(sys.id)

    return <SectionOfFAQResolved {...section} />
}

/**
 * Renders a section of FAQs
 */
const SectionOfFAQResolved: FunctionComponent<FaqSectionFragment> = ({
    heading,
    moreLink,
    faqs: { items } = EMPTY_COLLECTION,
    section: { title, subtitle } = EMPTY_SECTION,
}) => {
    const id = title ? titleToAnchor(title) : undefined
    const renderSchema = useContext(FaqSchemaContext)

    // Build FAQ schema for rich search results
    const faqSchema: FAQPage = {
        '@type': 'FAQPage',
        mainEntity: items
            .filter((item) => item.question && item.content?.content?.json)
            .map((item) => ({
                '@type': 'Question',
                name: item.question!,
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: documentToPlainTextString(
                        item.content!.content!.json,
                    ),
                },
            })),
    }

    return (
        <div id={id}>
            {renderSchema && items.length > 0 && (
                <JsonLd<FAQPage> data={faqSchema} />
            )}
            <Container className={styles.section}>
                <Box pad={[]}>
                    {(heading || title) && (
                        <div className={styles.header}>
                            <Markdown
                                markdown={heading ?? title}
                                tag='h2'
                                typeStyle={{
                                    _: 'heading-sm',
                                    md: 'heading-xl',
                                }}
                            />
                        </div>
                    )}
                    {subtitle && <Paragraph>{subtitle}</Paragraph>}

                    {items.map(({ anchor, question, content }, i: number) => (
                        <Details
                            key={question ?? i}
                            summary={question}
                            anchor={anchor}
                        >
                            {content?.content && (
                                <RichText
                                    source={content.content}
                                    renderers={renderers}
                                />
                            )}
                        </Details>
                    ))}
                    {moreLink?.path && (
                        <Link href={moreLink.path}>
                            <div
                                className={classNames(styles['external-link'])}
                            >
                                <Text
                                    typeStyle='body-lg'
                                    tag='div'
                                    bold
                                    color='currentColor'
                                >
                                    {moreLink.title}
                                </Text>
                                <Arrow />
                            </div>
                        </Link>
                    )}
                </Box>
            </Container>
        </div>
    )
}

const renderers: CustomRenderers = {
    listItem: (_, children) => (
        <List.Item className={styles.item}>{children}</List.Item>
    ),
    paragraph: (_, children) => (
        <Paragraph className={styles.paragraph}>{children}</Paragraph>
    ),
    hr: () => <hr className={styles.hr} />,
}

export default SectionOfFAQs
